import React, { useMemo, useState } from 'react';
import { mod, seededRandom } from './index';

// High Card: each player draws cards from a deterministic 1-20 deck. The
// player with the higher INT score gets to draw extras and keep the best.
//   modifier diff <= 1   → both draw 1
//   modifier diff 2-3    → higher-INT side draws 2, keeps best
//   modifier diff >= 4   → higher-INT side draws 3, keeps best
//
// The submitted score is { cards: [...], best } so the result screen can
// show every card drawn — no "rigged" doubts; players can see all the
// information.

function drawCountFor(myMod, theirMod) {
  const diff = myMod - theirMod;
  if (diff >= 4) return 3;
  if (diff >= 2) return 2;
  return 1;
}

export default {
  id: 'highCard',
  label: 'High Card',
  stat: 'int',
  icon: '🃏',
  description: 'Both draw a card 1-20. Higher INT draws extras and keeps the best.',
  paramsFromStats(myScore, theirScore) {
    return { drawCount: drawCountFor(mod(myScore), mod(theirScore)) };
  },
  // Compare the `best` value out of each player's draws.
  computeWinner(fromScore, toScore) {
    const a = fromScore?.best ?? -Infinity;
    const b = toScore?.best   ?? -Infinity;
    if (a > b) return 'from';
    if (b > a) return 'to';
    return 'tie';
  },
  // For the default scoreboard fallback, surface the kept value.
  scoreNumber(score) {
    return score?.best ?? null;
  },
  ResultDisplay({ challenge, mySide }) {
    const opponentName = mySide === 'from' ? challenge.toName : challenge.fromName;
    const myScore       = mySide === 'from' ? challenge.fromScore : challenge.toScore;
    const oppScore      = mySide === 'from' ? challenge.toScore   : challenge.fromScore;
    const myWon         = challenge.winner === mySide;
    const oppWon        = challenge.winner === (mySide === 'from' ? 'to' : 'from');

    return (
      <div style={resultStyles.wrap}>
        <PlayerCardsBlock
          name="You"
          cards={myScore?.cards || []}
          best={myScore?.best}
          highlight={myWon}
        />
        <div style={resultStyles.vs}>vs</div>
        <PlayerCardsBlock
          name={opponentName}
          cards={oppScore?.cards || []}
          best={oppScore?.best}
          highlight={oppWon}
        />
      </div>
    );
  },
  Component({ challenge, mySide, params, seed, onScore }) {
    const [drawn, setDrawn] = useState(false);
    const drawCount = params?.drawCount || 1;

    // Deterministic deck: each side gets up to 3 cards from disjoint slots
    // of the same seeded sequence. Both sides use the same seed but XOR
    // with a side-specific constant so the LCGs are independent.
    //
    // Side offsets must be bit-different enough that the LCG outputs
    // decorrelate. With offsets 0 vs 16 the LCG's first output differed by
    // only `16 * 1664525 / 2^32 ≈ 0.0063`, so for a 1d20 (bucket width 0.05)
    // both sides almost always drew the same card — the game always tied.
    // Using large opposite-bit constants forces wholly different seeds.
    const cards = useMemo(() => {
      const sideOffset = mySide === 'from' ? 0x5A5A5A5A : 0xA5A5A5A5;
      const rng = seededRandom((seed | 0) ^ sideOffset);
      // Warm up so even pathological seed values don't bias the first roll.
      for (let i = 0; i < 4; i++) rng();
      const out = [];
      for (let i = 0; i < drawCount; i++) {
        out.push(1 + Math.floor(rng() * 20));
      }
      return out;
    }, [seed, mySide, drawCount]);

    const best = useMemo(() => Math.max(...cards), [cards]);

    const handleDraw = () => {
      if (drawn) return;
      setDrawn(true);
      onScore({ cards, best });
    };

    return (
      <div style={styles.wrap}>
        <div style={styles.title}>High Card</div>
        <div style={styles.subtitle}>
          {drawCount === 1 && 'Draw a card 1-20.'}
          {drawCount === 2 && 'INT advantage — draw 2, keep best.'}
          {drawCount === 3 && 'Big INT advantage — draw 3, keep best.'}
        </div>
        {!drawn ? (
          <button style={styles.drawBtn} onClick={handleDraw}>
            🃏 Tap to draw
          </button>
        ) : (
          <>
            <div style={styles.cardsRow}>
              {cards.map((c, i) => {
                const isBest = c === best;
                return (
                  <div key={i} style={{ ...styles.card, ...(isBest ? styles.cardBest : styles.cardDim) }}>
                    <span style={styles.cardValue}>{c}</span>
                    {isBest && <span style={styles.cardKept}>kept</span>}
                  </div>
                );
              })}
            </div>
            <div style={styles.totalLine}>
              Your card: <span style={styles.totalValue}>{best}</span>
            </div>
            <div style={styles.waitingHint}>
              Waiting for opponent…
            </div>
          </>
        )}
      </div>
    );
  },
};

function PlayerCardsBlock({ name, cards, best, highlight }) {
  return (
    <div style={{ ...resultStyles.block, ...(highlight ? resultStyles.blockWin : {}) }}>
      <div style={resultStyles.name}>{name}</div>
      <div style={resultStyles.cardsRow}>
        {cards.length === 0 && <div style={resultStyles.empty}>—</div>}
        {cards.map((c, i) => {
          const isBest = c === best;
          return (
            <div key={i} style={{ ...resultStyles.card, ...(isBest ? resultStyles.cardKept : resultStyles.cardDim) }}>
              <span style={resultStyles.cardVal}>{c}</span>
            </div>
          );
        })}
      </div>
      <div style={resultStyles.bestLabel}>kept <strong>{best ?? '—'}</strong></div>
    </div>
  );
}

const styles = {
  wrap:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: 18 },
  title:     { fontSize: 22, fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.06em' },
  subtitle:  { fontSize: 13, color: '#9a8b6e', textAlign: 'center', maxWidth: 320 },
  drawBtn:   { padding: '24px 32px', background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '2px solid #c4a44a', color: '#1a0e00', fontSize: 18, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 8, boxShadow: '0 6px 20px rgba(212,168,71,0.4)', letterSpacing: '0.04em' },
  cardsRow:  { display: 'flex', gap: 10 },
  card:      { width: 70, height: 100, background: 'linear-gradient(180deg, #1a2a48, #0e1528)', border: '2px solid #4a3d28', borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, transition: 'border-color 0.2s, box-shadow 0.2s' },
  cardBest:  { borderColor: '#d4a847', boxShadow: '0 0 16px rgba(212,168,71,0.45)' },
  cardDim:   { opacity: 0.45 },
  cardValue: { fontSize: 32, fontWeight: 800, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace" },
  cardKept:  { fontSize: 9, color: '#d4a847', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' },
  totalLine: { fontSize: 16, color: '#f0e6d0', fontWeight: 700, fontFamily: "'Press Start 2P', monospace" },
  totalValue:{ color: '#d4a847', fontSize: 22, fontWeight: 800, marginLeft: 6 },
  waitingHint: { fontSize: 12, color: '#9a8b6e', fontStyle: 'italic' },
};

const resultStyles = {
  wrap:      { display: 'flex', alignItems: 'stretch', gap: 12, justifyContent: 'center' },
  block:     { flex: '0 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '10px 12px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 6 },
  blockWin:  { borderColor: '#d4a847', background: 'rgba(212,168,71,0.08)', boxShadow: '0 0 12px rgba(212,168,71,0.35)' },
  name:      { fontSize: 11, fontWeight: 700, color: '#9a8b6e', letterSpacing: '0.06em', textTransform: 'uppercase' },
  cardsRow:  { display: 'flex', gap: 6 },
  card:      { width: 38, height: 56, background: 'linear-gradient(180deg, #1a2a48, #0e1528)', border: '1px solid #4a3d28', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardKept:  { borderColor: '#d4a847', boxShadow: '0 0 8px rgba(212,168,71,0.45)' },
  cardDim:   { opacity: 0.45 },
  cardVal:   { fontSize: 20, fontWeight: 800, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace" },
  empty:     { color: '#6a5d45', fontSize: 14 },
  bestLabel: { fontSize: 11, color: '#9a8b6e' },
  vs:        { display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 800, color: '#9a8b6e' },
};
